import { Loader2 } from "lucide-react";
import type React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type DataAttributes = {
  [key: `data-${string}`]: string | number | boolean | undefined;
};

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  loadingText?: string;
  variant?: "default" | "destructive" | "secondary" | "teal";
  icon?: React.ReactNode;
  closeOnConfirm?: boolean;
  className?: string;
  contentProps?: React.ComponentPropsWithoutRef<typeof AlertDialogContent> &
    DataAttributes;
}

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  isLoading = false,
  confirmDisabled = false,
  loadingText,
  variant = "default",
  icon,
  closeOnConfirm = false,
  className,
  children,
  contentProps,
}: ConfirmationDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        {...contentProps}
        className={cn(className, contentProps?.className)}
      >
        <AlertDialogHeader>
          <div className="flex items-start gap-3 text-left">
            {icon && (
              <div className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full bg-muted">
                {icon}
              </div>
            )}
            <div className="min-w-0 space-y-2">
              <AlertDialogTitle>{title}</AlertDialogTitle>
              {description && (
                <AlertDialogDescription>{description}</AlertDialogDescription>
              )}
            </div>
          </div>
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isLoading || confirmDisabled}
            onClick={async (e) => {
              e.preventDefault();
              await onConfirm();
              if (closeOnConfirm) {
                onOpenChange(false);
              }
            }}
            className={cn(
              buttonVariants({
                variant,
              }),
              "flex items-center justify-center",
            )}
          >
            {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
            {isLoading && loadingText ? loadingText : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
